import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Clock3, Info, Trash2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@lightning/ui/popover';
import type { DojoBeat, DojoHighlightKind, DojoLearnerMark, DojoUnit } from '@/types/dojo';
import {
  LEARNER_MARK_KINDS,
  LEARNER_MARK_KIND_LABEL,
  buildMarkedParagraphs,
  createLearnerMark,
  normalizeMarkRange,
  removeMark,
  resolveSelectionRange,
  setMarkKind,
  upsertMark,
  type LearnerMarkRange,
  type ParagraphElement,
} from '@/lib/learnerMarks';
import { cn } from '@/lib/utils';
import { HighlightLegend } from './HighlightLegend';

// The learner marks the vignette themselves on this beat, and their own marks are
// the only thing this panel ever paints. The authored spans are the answer key:
// they stay on the server, inform Howie's coaching, and are never shown to the
// learner — being told what you missed turns the exercise into a graded test.
const MARKING_BEAT: DojoBeat = 'highlight';
const BEATS_WITH_EXEMPLAR: DojoBeat[] = ['react_well', 'react_better', 'highlight'];
const BEATS_WITH_ENACT: DojoBeat[] = ['enact', 'chiddush', 'completed'];

const NO_MARKS: DojoLearnerMark[] = [];

type ExemplarMode = 'clean' | 'marking' | 'review';

interface VignettePanelProps {
  unit: DojoUnit;
  beat: DojoBeat;
  roundControls?: ReactNode;
  footer?: ReactNode;
  /** The learner's own marks. Owned by the page so they survive a beat change. */
  marks?: DojoLearnerMark[];
  /** Omitted on read-only surfaces; marking is off without it. */
  onMarksChange?: (marks: DojoLearnerMark[]) => void;
  className?: string;
  /** Remove the card shell when the passage already lives inside a workspace pane. */
  embedded?: boolean;
}

type PanelView = 'title' | 'exemplar' | 'enact';

function splitEnactFrame(value: string) {
  const marker = value.search(/\bHold constant\b/i);
  const rawChange = (marker === -1 ? value : value.slice(0, marker))
    .trim()
    .replace(/[.\s]+$/, '');
  const separator = rawChange.indexOf(':');
  const description = separator === -1 ? rawChange : rawChange.slice(separator + 1).trim();
  const change = description
    ? `${description.charAt(0).toUpperCase()}${description.slice(1)}`
    : description;

  return {
    dimension: separator === -1 ? '' : rawChange.slice(0, separator).trim(),
    change,
    constants: marker === -1
      ? ''
      : value.slice(marker).replace(/^Hold constant\s*/i, '').trim(),
  };
}

function getView(beat: DojoBeat): PanelView {
  if (BEATS_WITH_ENACT.includes(beat)) return 'enact';
  if (BEATS_WITH_EXEMPLAR.includes(beat)) return 'exemplar';
  return 'title';
}

export function VignettePanel({
  unit,
  beat,
  roundControls,
  footer,
  marks = NO_MARKS,
  onMarksChange,
  className,
  embedded = false,
}: VignettePanelProps) {
  const view = getView(beat);
  const canMark = beat === MARKING_BEAT && typeof onMarksChange === 'function';
  // `review` = the learner's own marks, still visible but no longer editable. They
  // are notes the learner took, and the client's whole point is that they can look
  // back at them while answering Howie's two questions and the flipped case.
  const mode: ExemplarMode = canMark ? 'marking' : marks.length > 0 ? 'review' : 'clean';

  const [pending, setPending] = useState<LearnerMarkRange | null>(null);
  const [editingMarkId, setEditingMarkId] = useState<string | null>(null);
  // On the enact beat the learner can flip between their move and the read they
  // just marked up.
  const [enactTab, setEnactTab] = useState<'enact' | 'exemplar'>('enact');

  const proseRef = useRef<HTMLDivElement>(null);
  const paragraphNodes = useRef(new Map<number, HTMLElement>());
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  // Focus follows an explicit open (the button, or a click on a mark). Pulling
  // focus while a drag-selection is still in flight would end the drag.
  const [autoFocusMenu, setAutoFocusMenu] = useState(false);

  const paragraphs = unit.exemplar.paragraphs;
  const enactFrame = useMemo(() => splitEnactFrame(unit.enact.flip_axis), [unit.enact.flip_axis]);

  // The enact beat opens on the flipped case itself; the learner can flip back to
  // the vignette they marked up if they want to re-read their own marks.
  useEffect(() => {
    if (view === 'enact') setEnactTab('enact');
    // Only when the view itself changes — flipping tabs must not be undone.
  }, [view]);

  useEffect(() => {
    if (!canMark) {
      setPending(null);
      setEditingMarkId(null);
    }
  }, [canMark]);

  const paragraphElements = useCallback(
    (): ParagraphElement[] =>
      Array.from(paragraphNodes.current, ([index, element]) => ({ index, element })),
    []
  );

  // Read the selection when the gesture ENDS, not on every selectionchange.
  //
  // Painting the pending mark re-renders the paragraph, which replaces the text
  // nodes the browser's selection is anchored to. Doing that on selectionchange
  // means doing it in the middle of a drag, under the live selection — the
  // browser then re-anchors what is left of the range to the element itself, and
  // the mark comes back running from the start of the paragraph.
  //
  // mouseup and keyup between them cover both paths the old listener served: the
  // pointer drag and shift+arrow. The offsets themselves were never wrong — the
  // resolver, the normalizer and the segment builder all return exactly the
  // selected span; the fault was mutating the DOM while the selection was still
  // being made.
  useEffect(() => {
    if (!canMark) return;

    const captureSelection = () => {
      const selection = proseRef.current?.ownerDocument.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

      const resolved = resolveSelectionRange(selection.getRangeAt(0), paragraphElements());
      if (!resolved) return;

      const normalized = normalizeMarkRange(
        paragraphs[resolved.paragraph],
        resolved.start,
        resolved.end
      );
      if (!normalized) return;

      setEditingMarkId(null);
      setPending({ paragraph: resolved.paragraph, ...normalized });
    };

    const document_ = proseRef.current?.ownerDocument ?? globalThis.document;
    document_.addEventListener('mouseup', captureSelection);
    document_.addEventListener('keyup', captureSelection);
    return () => {
      document_.removeEventListener('mouseup', captureSelection);
      document_.removeEventListener('keyup', captureSelection);
    };
  }, [canMark, paragraphElements, paragraphs]);

  const closeMenu = useCallback(() => {
    setPending(null);
    setEditingMarkId(null);
  }, []);

  useEffect(() => {
    if (!pending && !editingMarkId) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };
    const document_ = proseRef.current?.ownerDocument ?? globalThis.document;
    document_.addEventListener('keydown', handleKeyDown);
    return () => document_.removeEventListener('keydown', handleKeyDown);
  }, [closeMenu, editingMarkId, pending]);

  const editingMark = editingMarkId
    ? marks.find((mark) => mark.id === editingMarkId) ?? null
    : null;
  const menuOpen = Boolean(pending || editingMark);
  const menuAnchorSelector = editingMark
    ? `[data-mark-id="${editingMark.id}"]`
    : pending
      ? '[data-pending="true"]'
      : null;

  useLayoutEffect(() => {
    if (!menuAnchorSelector) {
      setMenuPosition(null);
      return;
    }
    const wrapper = proseRef.current;
    const anchor = wrapper?.querySelector(menuAnchorSelector) as HTMLElement | null;
    if (!wrapper || !anchor) {
      setMenuPosition(null);
      return;
    }
    const wrapperRect = wrapper.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    setMenuPosition({
      top: anchorRect.bottom - wrapperRect.top + 6,
      left: Math.max(0, anchorRect.left - wrapperRect.left),
    });
  }, [editingMarkId, marks, menuAnchorSelector, pending]);

  useEffect(() => {
    if (!menuOpen || !autoFocusMenu) return;
    menuRef.current?.querySelector('button')?.focus({ preventScroll: true });
    setAutoFocusMenu(false);
  }, [autoFocusMenu, menuOpen]);

  const commitKind = useCallback(
    (kind: DojoHighlightKind) => {
      if (!onMarksChange) return;

      if (editingMark) {
        onMarksChange(setMarkKind(marks, editingMark.id, kind));
        closeMenu();
        return;
      }
      if (!pending) return;

      const mark = createLearnerMark(paragraphs, pending, kind);
      if (mark) onMarksChange(upsertMark(marks, mark));
      closeMenu();
      proseRef.current?.ownerDocument.getSelection()?.removeAllRanges();
    },
    [closeMenu, editingMark, marks, onMarksChange, paragraphs, pending]
  );

  const handleRemoveMark = useCallback(() => {
    if (!onMarksChange || !editingMark) return;
    onMarksChange(removeMark(marks, editingMark.id));
    closeMenu();
  }, [closeMenu, editingMark, marks, onMarksChange]);

  const rendered = useMemo(
    () =>
      buildMarkedParagraphs(
        paragraphs,
        mode === 'clean' ? NO_MARKS : marks,
        [],
        canMark ? pending : null
      ),
    [canMark, marks, mode, paragraphs, pending]
  );


  const registerParagraph = (index: number) => (node: HTMLElement | null) => {
    if (node) paragraphNodes.current.set(index, node);
    else paragraphNodes.current.delete(index);
  };

  // Shared by pointer and keyboard so the span behaves like the button it replaced.
  const openMarkEditor = (markId: string) => () => {
    setPending(null);
    setEditingMarkId((prev) => (prev === markId ? null : markId));
    setAutoFocusMenu(true);
  };

  const renderMarkMenu = () => {
    if (!menuOpen) return null;
    const quote = editingMark
      ? editingMark.quote
      : pending
        ? paragraphs[pending.paragraph]?.slice(pending.start, pending.end) ?? ''
        : '';

    return (
      <div
        ref={menuRef}
        role="group"
        aria-label={editingMark ? 'Change this mark' : 'Mark this passage'}
        className="absolute z-20 flex items-center gap-1 rounded-lg border border-how-space-deep/10 bg-white p-1 shadow-popover"
        style={menuPosition ? { top: menuPosition.top, left: menuPosition.left } : undefined}
      >
        {LEARNER_MARK_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            onClick={() => commitKind(kind)}
            aria-pressed={editingMark ? editingMark.kind === kind : undefined}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-how-space-deep transition-colors hover:bg-how-paper',
              editingMark?.kind === kind && 'bg-how-paper'
            )}
          >
            <span className="unit-legend-swatch" data-kind={kind} aria-hidden="true" />
            {LEARNER_MARK_KIND_LABEL[kind]}
          </button>
        ))}

        {editingMark && (
          <button
            type="button"
            onClick={handleRemoveMark}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-how-text-on-paper/70 transition-colors hover:bg-how-paper hover:text-how-space-deep"
          >
            <Trash2 size={13} aria-hidden="true" />
            Remove
          </button>
        )}

        <button
          type="button"
          onClick={closeMenu}
          className="rounded-md px-2 py-1.5 text-xs text-how-text-on-paper/50 transition-colors hover:text-how-space-deep"
        >
          Cancel
        </button>

        <span className="sr-only">{quote}</span>
      </div>
    );
  };

  // Keep the marking direction singular. The questions return in conversation
  // after the learner has made their own first read.
  const leaderName = unit.exemplar.leader_name?.trim() || 'the leader';

  const renderExemplar = (withPrompts: boolean) => {
    return (
      <div className="space-y-5">
        {withPrompts && (
          <div className="border-l-2 border-how-accent-warm/70 bg-white/45 px-4 py-3.5">
            <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.09em] text-how-accent-warm">
              Your focus
            </p>
            <p className="text-[15px] font-medium leading-6 text-how-space-deep/85">
              {mode === 'marking' ? (
                <>
                  Mark what {leaderName} did well in green, and what could have gone further in yellow.
                </>
              ) : (
                `Consider what ${leaderName} did well, and what could have gone further.`
              )}
            </p>
          </div>
        )}

        {mode === 'review' && <HighlightLegend interactive={false} />}

        <div className="relative space-y-4" ref={proseRef}>
          {rendered.map((paragraph) => {
            return (
              <div key={paragraph.index}>
                <p
                  ref={registerParagraph(paragraph.index)}
                  data-paragraph={paragraph.index}
                  className="text-[16px] leading-[29px] text-how-text-on-paper"
                >
                  {paragraph.segments.map((segment, segmentIndex) => {
                    if (segment.pending) {
                      return (
                        <mark
                          key={segmentIndex}
                          className="unit-mark"
                          data-pending="true"
                          data-state="pending"
                        >
                          {segment.text}
                        </mark>
                      );
                    }

                    if (!segment.mark) {
                      return <span key={segmentIndex}>{segment.text}</span>;
                    }

                    if (mode === 'review') {
                      return (
                        <mark
                          key={segmentIndex}
                          className="unit-mark"
                          data-kind={segment.mark.kind}
                          data-state="own"
                          data-mark-id={segment.mark.id}
                        >
                          {segment.text}
                        </mark>
                      );
                    }

                    // Marking: the learner's own marks, each one editable.
                    if (mode === 'marking' && segment.mark) {
                      const mark = segment.mark;
                      return (
                        <mark
                          key={segmentIndex}
                          className="unit-mark"
                          data-kind={mark.kind}
                          data-state="own"
                          data-mark-id={mark.id}
                        >
                          <span
                            role="button"
                            tabIndex={0}
                            className="unit-mark-trigger"
                            aria-label={`${LEARNER_MARK_KIND_LABEL[mark.kind]}: ${mark.quote}. Change or remove this mark.`}
                            aria-expanded={editingMarkId === mark.id}
                            onClick={openMarkEditor(mark.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openMarkEditor(mark.id)();
                              }
                            }}
                          >
                            {segment.text}
                          </span>
                        </mark>
                      );
                    }

                    return <span key={segmentIndex}>{segment.text}</span>;
                  })}
                </p>
              </div>
            );
          })}

          {renderMarkMenu()}
        </div>

      </div>
    );
  };

  return (
    <article
      className={cn(
        embedded
          ? 'bg-transparent'
          : 'rounded-2xl border border-how-space-deep/[0.05] bg-how-paper/55 px-4 py-4 sm:px-8 sm:py-7',
        className
      )}
      aria-label={unit.title}
    >
      {roundControls && (
        <div className="mb-3 border-b border-how-space-deep/[0.06] pb-3 sm:mb-5 sm:pb-4">
          {roundControls}
        </div>
      )}
      {!embedded && (
        <header className="flex flex-col items-start gap-1 sm:flex-row sm:justify-between sm:gap-5">
          <span className="text-[11px] font-medium uppercase leading-5 tracking-[0.06em] text-how-accent-warm">
            {unit.imperative_label}
          </span>
          {beat === 'intake' && <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] leading-5 text-how-text-on-paper/50">
            <Clock3 size={12} aria-hidden="true" />
            About {unit.estimated_minutes} min
          </span>}
        </header>
      )}

      <div className={cn('items-center justify-between gap-5', embedded ? 'hidden lg:flex' : 'mt-2 flex sm:mt-3')}>
        <h2 className="font-body text-[24px] font-medium leading-[30px] text-how-space-deep">
          {unit.title}
        </h2>
        {embedded && beat === 'intake' && (
          <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] leading-5 text-how-text-on-paper/50">
            <Clock3 size={12} aria-hidden="true" />
            About {unit.estimated_minutes} min
          </span>
        )}
      </div>

      <div className={cn('max-w-[65ch]', embedded ? 'lg:mt-5' : 'mt-3 sm:mt-5')}>
        {view === 'title' && (
          <p className="text-[14px] leading-6 text-how-text-on-paper/70">
            Two questions, then a short read.
          </p>
        )}

        {view === 'exemplar' && renderExemplar(true)}

        {view === 'enact' && (
          <div className="space-y-5">
            <div className="flex justify-end">
              <div className="inline-flex rounded-full bg-how-space-deep/[0.06] p-0.5" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={enactTab === 'enact'}
                  onClick={() => setEnactTab('enact')}
                  className={cn(
                    'rounded-full px-3 py-1 text-[11px] font-medium transition-colors',
                    enactTab === 'enact'
                      ? 'bg-white text-how-space-deep shadow-sm'
                      : 'text-how-text-on-paper/60 hover:text-how-space-deep'
                  )}
                >
                  Your move
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={enactTab === 'exemplar'}
                  onClick={() => setEnactTab('exemplar')}
                  className={cn(
                    'rounded-full px-3 py-1 text-[11px] font-medium transition-colors',
                    enactTab === 'exemplar'
                      ? 'bg-white text-how-space-deep shadow-sm'
                      : 'text-how-text-on-paper/60 hover:text-how-space-deep'
                  )}
                >
                  The story
                </button>
              </div>
            </div>

            <Popover key={unit.scenario_id + beat}>
              <PopoverTrigger asChild>
                <button type="button" className="inline-flex min-h-11 items-center gap-1.5 rounded-md text-[12px] text-how-text-secondary hover:text-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/60">
                  <Info aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Hint
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" sideOffset={8} aria-label="Practice hint"
                className="max-h-[min(65vh,420px)] w-[min(360px,calc(100vw-32px))] overflow-y-auto rounded-xl border-how-space-deep/10 bg-how-paper-warm p-4 font-body text-how-space-deep shadow-popover">
              <p className="mb-1.5 text-sm font-medium text-how-space-deep">
                What changed
              </p>
              {enactFrame.dimension && (
                <p className="text-[15px] font-medium leading-6 text-how-space-deep/85">
                  {enactFrame.dimension}
                </p>
              )}
              <p className={cn(
                'text-[14px] leading-[23px] text-how-text-secondary',
                enactFrame.dimension && 'mt-0.5'
              )}>
                {enactFrame.change}
              </p>
              {enactFrame.constants && (
                <details className="group mt-3 border-t border-how-space-deep/[0.07] pt-3">
                  <summary className="cursor-pointer list-none text-[12px] font-medium text-how-text-secondary transition-colors hover:text-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/20 [&::-webkit-details-marker]:hidden">
                    <span className="group-open:hidden">What stayed the same</span>
                    <span className="hidden group-open:inline">Hide what stayed the same</span>
                  </summary>
                  <p className="mt-2 text-[13px] leading-[22px] text-how-text-secondary">
                    {enactFrame.constants}
                  </p>
                </details>
              )}
              </PopoverContent>
            </Popover>

            {enactTab === 'enact' ? (
              <div className="space-y-4">
                {unit.enact.paragraphs.map((paragraph, index) => (
                  <p key={index} className="text-[16px] leading-[29px] text-how-text-on-paper">
                    {paragraph}
                  </p>
                ))}
                <p className="border-t border-how-space-deep/10 pt-4 text-[15px] font-medium leading-[26px] text-how-space-deep">
                  {unit.enact.question}
                </p>
              </div>
            ) : (
              renderExemplar(false)
            )}
          </div>
        )}
      </div>

      {footer && (
        <div className="mt-7 border-t border-how-space-deep/[0.06] pt-5">
          {footer}
        </div>
      )}
    </article>
  );
}

export default VignettePanel;
